import { useQuery } from '@tanstack/react-query'
import { useId, useState } from 'react'

import { useApiClient } from '@/api/ApiClientProvider'
import { LIFECYCLE_MAX_COMPETITIONS, type LeakageBasis, type LifecycleAlign } from '@/api/revenueLeakage'
import { Card, EmptyState, LoadingBlock, SegmentedControl, type FormatOptions } from '@/design'
import type { GlobalFilters } from '@/lib/filters'
import { filtersKey } from '@/lib/useGlobalFilters'

import { useRevenueLifecycle } from '../leakage/queries'
import { LIFECYCLE_UNAVAILABLE_TEXT, QUESTIONS } from '../leakage/wording'
import { LifecycleChart, SeriesKey, type ChartSeries } from './LifecycleChart'
import { byLaunch, defaultSelection, toggled, type PickableCompetition } from './selection'
import '../charts.css'
import './lifecycle.css'

/** What the page's shell passes the panel (`slots.tsx`'s `RevenueSlotProps`). */
export interface LifecyclePanelProps {
  filters: GlobalFilters
  basis: LeakageBasis
  money: FormatOptions
}

/** The competitions the picker offers: the list route's, newest launch first (at most 200). */
function usePickable(filters: GlobalFilters) {
  const client = useApiClient()
  return useQuery({
    queryKey: ['revenue-stage-b', 'pickable', client.kind, filtersKey(filters)],
    queryFn: () => client.competitions({ filters, sort: 'end_at', order: 'desc', limit: 200 }),
    select: (envelope) => byLaunch<PickableCompetition>(envelope.data.rows),
  })
}

/**
 * Each slot is the competition's for as long as it is chosen: taking one out never
 * repaints the others (colour follows the competition, not its place in the list).
 */
function withSlots(chosen: readonly string[], previous: ReadonlyMap<string, number>): Map<string, number> {
  const slots = new Map<string, number>()
  for (const id of chosen) {
    const kept = previous.get(id)
    if (kept !== undefined) slots.set(id, kept)
  }
  for (const id of chosen) {
    if (slots.has(id)) continue
    const used = new Set(slots.values())
    const free = Array.from({ length: LIFECYCLE_MAX_COMPETITIONS }, (_, index) => index + 1).find((slot) => !used.has(slot)) ?? 1
    slots.set(id, free)
  }
  return slots
}

/**
 * The Timing tab's lifecycle panel (`GET /analytics/revenue/lifecycle`): up to six
 * competitions' revenue by day since launch — or counting down to the draw — each over
 * its own dates, so a competition on sale can be read against its predecessors. Opens on
 * the newest live competition and the newest launches of its category.
 */
export function LifecyclePanel({ filters, basis, money }: LifecyclePanelProps) {
  const pickable = usePickable(filters)
  const [chosen, setChosen] = useState<string[] | null>(null)
  const [slots, setSlots] = useState<Map<string, number>>(new Map())
  const [align, setAlign] = useState<LifecycleAlign>('launch')
  const ids = chosen ?? (pickable.data ? defaultSelection(pickable.data) : [])
  const slotted = withSlots(ids, slots)
  const lifecycle = useRevenueLifecycle(filters, basis, ids, align)

  const choose = (id: string) => {
    const next = toggled(ids, id)
    setSlots(withSlots(next, slotted))
    setChosen(next)
  }

  const curves = lifecycle.data?.data
  // While the next answer loads, the previous one stands in: draw only what is still chosen,
  // so a removed competition never shows in a colour now someone else's.
  const series: ChartSeries[] = (curves?.curves ?? []).flatMap((curve) => {
    const slot = slotted.get(curve.competitionId)
    return slot === undefined ? [] : [{ curve, slot }]
  })
  const names = new Map((pickable.data ?? []).map((competition) => [competition.competitionId, competition.name]))

  return (
    <Card
      className="rv-panel rv-panel--side rl-life-panel"
      title="Revenue by day since launch"
      subtitle={QUESTIONS.lifecycle}
      busy={lifecycle.isFetching}
      actions={
        <SegmentedControl
          label="Count days from"
          options={[
            { value: 'launch', label: 'Launch' },
            { value: 'draw', label: 'Draw' },
          ]}
          value={align}
          onChange={setAlign}
        />
      }
    >
      <Picker pickable={pickable.data} pending={pickable.isPending} chosen={ids} slots={slotted} onToggle={choose} />
      {ids.length === 0 ? (
        pickable.isPending ? (
          <LoadingBlock rows={4} label="Loading competitions" />
        ) : (
          <EmptyState variant="no-results" compact title="No competition chosen" description="Choose up to six competitions to compare their revenue from launch." />
        )
      ) : lifecycle.isPending ? (
        <LoadingBlock rows={5} label="Loading each competition's days" />
      ) : !curves ? (
        <EmptyState variant="outage" announce compact description="The competitions' days could not be loaded just now. Try again in a moment." />
      ) : (
        <>
          {series.length > 0 ? (
            <LifecycleChart series={series} align={align} money={{ ...money, currency: curves.currency }} />
          ) : (
            <EmptyState variant="no-results" compact title="Nothing to draw" description="None of the chosen competitions has days to show." />
          )}
          {curves.notAvailable.length > 0 ? (
            <ul className="rl-life__unavailable">
              {curves.notAvailable.map((entry) => (
                <li key={entry.competitionId}>
                  {names.get(entry.competitionId) ?? 'A chosen competition'} {LIFECYCLE_UNAVAILABLE_TEXT[entry.reason]}, so it is not drawn.
                </li>
              ))}
            </ul>
          ) : null}
          <p className="rl-life__note">Each line runs over its competition’s own days, whatever the page’s dates.</p>
        </>
      )}
    </Card>
  )
}

/** The chosen competitions, each removable, and the list to add from (six at most). */
function Picker({
  pickable,
  pending,
  chosen,
  slots,
  onToggle,
}: {
  pickable: readonly PickableCompetition[] | undefined
  pending: boolean
  chosen: readonly string[]
  slots: ReadonlyMap<string, number>
  onToggle: (id: string) => void
}) {
  const listId = useId()
  const full = chosen.length >= LIFECYCLE_MAX_COMPETITIONS
  const names = new Map((pickable ?? []).map((competition) => [competition.competitionId, competition.name]))
  return (
    <div className="rl-life__picker">
      {chosen.length > 0 ? (
        <ul className="rl-life__chosen" aria-label="Competitions compared">
          {chosen.map((id) => (
            <li key={id} className="rl-life__chip">
              <SeriesKey slot={slots.get(id) ?? 1} />
              <span>{names.get(id) ?? id}</span>
              <button type="button" className="rl-life__remove" aria-label={`Stop comparing ${names.get(id) ?? id}`} onClick={() => onToggle(id)}>
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <details className="rl-life__choose">
        <summary>
          Choose competitions ({chosen.length} of {LIFECYCLE_MAX_COMPETITIONS})
        </summary>
        {pending ? (
          <LoadingBlock rows={3} label="Loading competitions" />
        ) : !pickable || pickable.length === 0 ? (
          <p className="rl-life__note">No competition with a launch date in this scope.</p>
        ) : (
          <fieldset className="rl-life__options" aria-describedby={listId}>
            <legend className="ci-visually-hidden">Competitions to compare</legend>
            <p id={listId} className="rl-life__note">
              {full ? 'Six are compared: take one out to add another.' : 'Newest launch first.'}
            </p>
            {pickable.map((competition) => {
              const checked = chosen.includes(competition.competitionId)
              return (
                <label key={competition.competitionId} className="rl-life__option">
                  <input type="checkbox" checked={checked} disabled={!checked && full} onChange={() => onToggle(competition.competitionId)} />
                  <span>{competition.name}</span>
                  <span className="rl-life__meta">
                    {competition.category ?? 'Uncategorised'} · {competition.startAt?.slice(0, 10)}
                  </span>
                </label>
              )
            })}
          </fieldset>
        )}
      </details>
    </div>
  )
}
